import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import { useDossierStore } from "../store/dossierStore";

export interface MembreAccess {
  loading: boolean;
  acces_gestion: boolean;
  acces_compta: boolean;
  role: "owner" | "daf" | "directeur_financier" | "comptable" | null;
}

const OPEN_ACCESS: MembreAccess = {
  loading: false,
  acces_gestion: true,
  acces_compta: true,
  role: null,
};

const LOADING_RESTRICTIVE: MembreAccess = {
  loading: true,
  acces_gestion: false,
  acces_compta: false,
  role: null,
};

/**
 * Droits membre pour le dossier actif. Ne souscrit qu’à `activeDossier.id` dans le store
 * (pas tout le store) pour éviter re-renders et re-fetch en boucle quand l’objet dossier change.
 */
export function useMembreAccess(): MembreAccess {
  const dossierId = useDossierStore((s) => s.activeDossier?.id ?? null);

  const [access, setAccess] = useState<MembreAccess>(LOADING_RESTRICTIVE);

  useEffect(() => {
    let cancelled = false;

    const fetchAccess = async () => {
      if (!dossierId) {
        if (!cancelled) setAccess(OPEN_ACCESS);
        return;
      }

      if (!cancelled) setAccess(LOADING_RESTRICTIVE);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;

      if (!user) {
        setAccess(OPEN_ACCESS);
        return;
      }

      const { data: membre, error } = await supabase
        .from("dossier_membres")
        .select("role, acces_gestion, acces_compta")
        .eq("dossier_id", dossierId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (!error && membre) {
        const acces_gestion = Boolean(membre.acces_gestion);
        const acces_compta = Boolean(membre.acces_compta);
        const role = membre.role as MembreAccess["role"];

        setAccess({
          loading: false,
          acces_gestion,
          acces_compta,
          role,
        });
        return;
      }

      const { data: ownedCabinets, error: ownedCabinetsError } = await supabase
        .from("cabinets")
        .select("id")
        .eq("user_id", user.id);

      if (cancelled) return;

      if (ownedCabinetsError) {
        // En cas d'erreur technique, ne pas bloquer la session utilisateur.
        setAccess(OPEN_ACCESS);
        return;
      }

      const cabinetIds = (ownedCabinets ?? []).map((c) => c.id).filter(Boolean);
      if (cabinetIds.length === 0) {
        setAccess({
          loading: false,
          acces_gestion: false,
          acces_compta: false,
          role: null,
        });
        return;
      }

      const { count, error: ownedDossierError } = await supabase
        .from("dossiers")
        .select("id", { count: "exact", head: true })
        .eq("id", dossierId)
        .in("cabinet_id", cabinetIds);

      if (cancelled) return;

      if (!ownedDossierError && (count ?? 0) > 0) {
        setAccess({
          loading: false,
          acces_gestion: true,
          acces_compta: true,
          role: "owner",
        });
        return;
      }

      if (ownedDossierError) {
        // Erreur réseau/RLS transitoire: éviter le "flash logout" côté UI.
        setAccess(OPEN_ACCESS);
        return;
      }

      setAccess({
        loading: false,
        acces_gestion: false,
        acces_compta: false,
        role: null,
      });
    };

    void fetchAccess();

    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  return useMemo(
    () => ({
      loading: access.loading,
      acces_gestion: access.acces_gestion,
      acces_compta: access.acces_compta,
      role: access.role,
    }),
    [access.loading, access.acces_gestion, access.acces_compta, access.role]
  );
}
